import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { useAuthStore } from './store/authStore';
import { useSocketStore } from './store/socketStore';
import { applyFarsiDocument } from './i18n/useTranslation';
import { isNativeApp } from './config';
import { startNativeNotifications, stopNativeNotifications } from './native/notifications';
import { startWebPush, stopWebPush, shouldShowNotificationButton } from './webPush';
import { startFocusSync, setPendingConversation } from './notifyFocus';
import { dispatchIncomingCall, dispatchNewMessage, dismissIncomingCallNotice } from './notifyClient';
import { translations } from './i18n/translations';
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';
import CallPage from './pages/CallPage';
import './App.css';

const Router = isNativeApp() ? HashRouter : BrowserRouter;

const PUSH_REASON_TEXT = {
  insecure: translations.pushNeedsDomain,
  'needs-domain': translations.pushNeedsDomain,
  'add-to-home': translations.pushAddToHome,
  denied: translations.pushDenied,
  unsupported: translations.pushUnsupported,
  vapid: translations.pushFailed,
  failed: translations.pushFailed,
  'needs-gesture': translations.enableNotificationsHint
};

function App() {
  const { token, user, checkToken } = useAuthStore();
  const { socket, initSocket } = useSocketStore();
  const [isLoading, setIsLoading] = useState(true);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [inAppAlert, setInAppAlert] = useState(null);
  const tokenRef = useRef(token);
  const alertTimer = useRef(null);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    applyFarsiDocument();
    checkToken();
    setIsLoading(false);
  }, [checkToken]);

  useEffect(() => startFocusSync(), []);

  useEffect(() => {
    if (token && !socket?.connected) {
      initSocket(token);
    }
  }, [token, socket?.connected, initSocket]);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let handle;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      const auth = tokenRef.current;
      if (auth) {
        initSocket(auth);
        startNativeNotifications(auth);
      }
    }).then((listener) => {
      handle = listener;
    });
    return () => {
      handle?.remove?.();
    };
  }, [initSocket]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === 'open-from-notification' && event.data.conversationId) {
        setPendingConversation(event.data.conversationId);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (token) {
      startNativeNotifications(token);
      if (!isNativeApp()) {
        startWebPush(token, { interactive: false }).then((result) => {
          if (result.ok) {
            setShowPushPrompt(false);
            setPushMessage('');
            return;
          }
          if (result.reason === 'needs-domain' || result.reason === 'insecure') {
            setShowPushPrompt(false);
            return;
          }
          if (result.reason === 'needs-gesture') {
            setShowPushPrompt(shouldShowNotificationButton());
            return;
          }
          if (result.reason === 'denied' || result.reason === 'unsupported') {
            setShowPushPrompt(false);
            return;
          }
          setShowPushPrompt(shouldShowNotificationButton());
        });
      }
    } else {
      stopNativeNotifications();
      stopWebPush();
      setShowPushPrompt(false);
      setPushMessage('');
    }
  }, [token]);

  useEffect(() => {
    if (!socket) return undefined;
    const showInApp = (copy) => {
      if (!copy?.inApp) return;
      setInAppAlert({ title: copy.title, body: copy.body });
      if (alertTimer.current) clearTimeout(alertTimer.current);
      alertTimer.current = setTimeout(() => setInAppAlert(null), 7000);
    };
    const onIncoming = (data) => {
      if (data.callerId === user?.id) return;
      dispatchIncomingCall(data, user?.id).then(showInApp);
    };
    const onMessage = (message) => {
      dispatchNewMessage(message, user?.id).then(showInApp);
    };
    const onStatus = (data) => {
      if (data?.callId && (data.status === 'rejected' || data.status === 'ended' || data.status === 'accepted')) {
        dismissIncomingCallNotice(data.callId);
      }
    };
    socket.on('incoming-call', onIncoming);
    socket.on('new-message', onMessage);
    socket.on('call-status', onStatus);
    return () => {
      socket.off('incoming-call', onIncoming);
      socket.off('new-message', onMessage);
      socket.off('call-status', onStatus);
    };
  }, [socket, user?.id]);

  const handleAllowNotifications = async () => {
    if (!token || pushBusy) return;
    setPushBusy(true);
    setPushMessage('');
    const result = await startWebPush(token, { interactive: true });
    if (result.ok) {
      setShowPushPrompt(false);
      setPushMessage('');
    } else if (result.reason === 'needs-domain' || result.reason === 'insecure') {
      setShowPushPrompt(false);
      setPushMessage(PUSH_REASON_TEXT[result.reason]);
    } else {
      setShowPushPrompt(shouldShowNotificationButton());
      setPushMessage(PUSH_REASON_TEXT[result.reason] || translations.pushFailed);
    }
    setPushBusy(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-canvas text-ink-muted">
        <span className="h-8 w-8 rounded-full border-2 border-line border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <Router>
      {token && showPushPrompt ? (
        <div className="fixed top-0 inset-x-0 z-[60] bg-primary text-white px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] shadow-nav">
          <p className="text-center font-semibold text-base leading-relaxed mb-3">
            {translations.enableNotificationsHint}
          </p>
          <button
            type="button"
            className="mx-auto block w-full max-w-sm min-h-14 rounded-2xl bg-white text-primary font-bold text-xl cursor-pointer active:scale-[0.99]"
            onClick={handleAllowNotifications}
            disabled={pushBusy}
          >
            {pushBusy ? translations.processing : translations.enableNotifications}
          </button>
          {pushMessage ? (
            <p className="mt-3 text-center text-sm leading-relaxed text-white/95">{pushMessage}</p>
          ) : null}
        </div>
      ) : null}
      {token && inAppAlert ? (
        <button
          type="button"
          className="fixed top-3 inset-x-3 z-[58] nest-card px-4 py-3 text-start shadow-nav cursor-pointer"
          onClick={() => setInAppAlert(null)}
        >
          <p className="font-bold text-ink">{inAppAlert.title}</p>
          <p className="text-sm text-ink-muted mt-1 leading-relaxed">{inAppAlert.body}</p>
        </button>
      ) : null}
      <div className={token && showPushPrompt ? 'pt-[11.5rem]' : undefined}>
        <Routes>
          <Route path="/login" element={token ? <Navigate to="/chat" /> : <LoginPage />} />
          <Route path="/chat" element={token ? <ChatPage /> : <Navigate to="/login" />} />
          <Route path="/call/:callId" element={token ? <CallPage /> : <Navigate to="/login" />} />
          <Route path="/" element={<Navigate to={token ? '/chat' : '/login'} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
